const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

// Short map URL domains — coordinates not extractable, show "Open map" link only
const SHORT_MAP_DOMAINS = /maps\.app\.goo\.gl|goo\.gl\/maps|maps\.me\/\?|yandex\.[a-z]+\/maps\//i;

const PATTERNS = [
    // Google Maps: ?q=lat,lng or ?ll=lat,lng (q/ll may be first or any param)
    {
        re: /(?:maps\.google\.com|google\.com\/maps)[^?]*\?(?:[^#&]*&)*(?:q|ll)=(-?\d+\.?\d*),(-?\d+\.?\d*)/i,
        coords: (m) => ({ lat: parseFloat(m[1]), lng: parseFloat(m[2]) }),
    },
    // Google Maps: @lat,lng,zoom
    {
        re: /(?:maps\.google\.com|google\.com\/maps).*@(-?\d+\.?\d*),(-?\d+\.?\d*)/i,
        coords: (m) => ({ lat: parseFloat(m[1]), lng: parseFloat(m[2]) }),
    },
    // OpenStreetMap: #map=zoom/lat/lng
    {
        re: /openstreetmap\.org[^#]*#map=\d+\/(-?\d+\.?\d*)\/(-?\d+\.?\d*)/i,
        coords: (m) => ({ lat: parseFloat(m[1]), lng: parseFloat(m[2]) }),
    },
    // OpenStreetMap: ?mlat=lat&mlon=lng (order not guaranteed)
    {
        re: /openstreetmap\.org.*[?&]mlat=(-?\d+\.?\d*).*[?&]mlon=(-?\d+\.?\d*)/i,
        coords: (m) => ({ lat: parseFloat(m[1]), lng: parseFloat(m[2]) }),
    },
];

function validCoords({ lat, lng }) {
    return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Tries to extract { lat, lng } from a map URL. Returns null if not a map link.
 * @param {string} url
 * @returns {{ lat: number, lng: number } | null}
 */
export function extractCoordsFromURL(url) {
    for (const p of PATTERNS) {
        const m = url.match(p.re);
        if (m) {
            const coords = p.coords(m);
            if (validCoords(coords)) return coords;
        }
    }
    return null;
}

/**
 * Scans a message body for the first map link.
 * Returns { lat, lng, url } with lat/lng=null for short links where coords can't be extracted.
 * @param {string} body
 * @returns {{ lat: number|null, lng: number|null, url: string } | null}
 */
export function extractMapFromBody(body) {
    if (!body) return null;
    for (const url of (body.match(URL_REGEX) || [])) {
        const coords = extractCoordsFromURL(url);
        if (coords) return { lat: coords.lat, lng: coords.lng, url };
        if (SHORT_MAP_DOMAINS.test(url)) return { lat: null, lng: null, url };
    }
    return null;
}

const TILE_SUBS = ['a', 'b', 'c'];

/**
 * Lat/lng → tile X/Y at given zoom.
 * @param {number} lat
 * @param {number} lng
 * @param {number} zoom
 * @returns {{ x: number, y: number }}
 */
export function latLngToTile(lat, lng, zoom) {
    const n = 2 ** zoom;
    const x = Math.floor((lng + 180) / 360 * n);
    const r = lat * Math.PI / 180;
    const y = Math.floor((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * n);
    return { x, y };
}

/**
 * Returns pixel offset of lat/lng within a tile grid starting at (tileX, tileY).
 * @param {number} lat
 * @param {number} lng
 * @param {number} zoom
 * @param {number} tileX
 * @param {number} tileY
 * @returns {{ x: number, y: number }}
 */
export function latLngToPixel(lat, lng, zoom, tileX, tileY) {
    const n = 2 ** zoom;
    const px = ((lng + 180) / 360 * n - tileX) * 256;
    const r = lat * Math.PI / 180;
    const py = ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * n - tileY) * 256;
    return { x: px, y: py };
}

/**
 * OSM tile URL for given z/x/y.
 * @param {number} z
 * @param {number} x
 * @param {number} y
 * @returns {string}
 */
export function tileUrl(z, x, y) {
    const sub = TILE_SUBS[(x + y) % 3];
    return `https://${sub}.tile.openstreetmap.org/${z}/${x}/${y}.png`;
}
